import React from "react";
import { isChunkError } from "./isChunkError";

const RETRY_KEY = "fn_chunk_retry";

const evictAndReload = async () => {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch {
    /* ignore */
  }
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    /* ignore */
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_r", Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
};

/**
 * Wrap a dynamic import so that a one-time chunk-load failure triggers
 * a cache eviction + hard reload. Prevents blank screens when a user
 * navigates to a route whose hashed chunk was replaced by a fresh deploy.
 */
export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkError(err)) throw err;

      const alreadyRetried =
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem(RETRY_KEY) === "done";

      if (alreadyRetried) throw err;

      try {
        sessionStorage.setItem(RETRY_KEY, "done");
      } catch {
        /* ignore */
      }

      console.warn("[Fennest] Chunk load failed, evicting caches and reloading");
      void evictAndReload();

      // Suspend forever while the reload happens.
      return await new Promise<{ default: T }>(() => {});
    }
  });
}
